import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createRawMaterialVendorAction,
    fetchAllRawMaterialVendorAction,
    updateRawMaterialVendorAction
} from '../../../../utils/redux/rawMaterialVendor/rawMaterialVendorActions';
import { fetchRawMaterialCategories } from '../../../../utils/api-service/get-services';
import DynamicFormModal from '../../../Modal/DynamicFormModal/DynamicFormModal';
import VendorPropertyDefinitionForm from './VendorPropertyDefinitionForm';
import { IoIosArrowDown } from "react-icons/io";
import { getMethod } from '../../../../utils/api-service/api-service';

const RawMaterialVendor = () => {
    const [openRow, setOpenRow] = useState(null);
    const [editingVendor, setEditingVendor] = useState(null);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [categoryTableColumns, setCategoryTableColumns] = useState([]);
    const dispatch = useDispatch();
    const { rawMaterialVendors, isLoading } = useSelector(state => state.rawMaterialVendor);

    useEffect(() => {
        if (!rawMaterialVendors || !rawMaterialVendors.length)
            dispatch(fetchAllRawMaterialVendorAction());
    }, [dispatch]);

    const getRawMaterialCategories = async () => {
        try {
            const response = await fetchRawMaterialCategories();
            return response?.categories;
        } catch (error) {
            console.log("Error: ", error);
            return [];
        }
    };

    const [modalConfig, setModalConfig] = useState({
        show: false,
        title: "",
        fields: [],
        initialData: {},
        dropdownData: {},
        onSubmit: null
    });

    const openAddModal = async () => {
        const categories = await getRawMaterialCategories();

        setModalConfig({
            show: true,
            title: "Add Raw Material Vendor",
            fields: [
                {
                    name: "name",
                    label: "Vendor Name",
                    type: "text",
                    required: true
                },
                {
                    name: "category",
                    label: "Raw Material Category",
                    type: "select",
                    required: true,
                    optionsKey: "categories"
                },
                {
                    name: "properties.location",
                    label: "Location (Country)",
                    type: "text",
                    placeholder: "e.g., IN, US, UK"
                },
                {
                    name: "properties.contactPerson",
                    label: "Contact Person",
                    type: "text"
                },
                {
                    name: "properties.email",
                    label: "Email",
                    type: "email"
                },
                {
                    name: "properties.phone",
                    label: "Phone",
                    type: "text"
                }
            ],
            initialData: {
                properties: {}
            },
            dropdownData: { categories: categories || [] },
            onSubmit: async (data) => {
                // Transform properties
                const payload = {
                    name: data.name,
                    category: data.category,
                    properties: data.properties || {},
                    vendorItemPropertyDefinitions: [] // Start with empty array
                };

                await dispatch(createRawMaterialVendorAction(payload));
                setModalConfig(prev => ({ ...prev, show: false }));
            }
        });
    };

    const openEditModal = async (vendor) => {
        const categories = await getRawMaterialCategories();

        setModalConfig({
            show: true,
            title: `Edit Vendor: ${vendor.name}`,
            fields: [
                {
                    name: "name",
                    label: "Vendor Name",
                    type: "text",
                    required: true
                },
                {
                    name: "category",
                    label: "Raw Material Category",
                    type: "select",
                    required: true,
                    optionsKey: "categories"
                },
                {
                    name: "properties.location",
                    label: "Location (Country)",
                    type: "text",
                    placeholder: "e.g., IN, US, UK"
                },
                {
                    name: "properties.contactPerson",
                    label: "Contact Person",
                    type: "text"
                },
                {
                    name: "properties.email",
                    label: "Email",
                    type: "email"
                },
                {
                    name: "properties.phone",
                    label: "Phone",
                    type: "text"
                }
            ],
            initialData: {
                name: vendor.name,
                category: vendor.category?._id || vendor.category,
                properties: vendor.properties || {}
            },
            dropdownData: { categories: categories || [] },
            onSubmit: async (data) => {
                const payload = {
                    name: data.name,
                    category: data.category,
                    properties: data.properties || {}
                };

                await dispatch(updateRawMaterialVendorAction(vendor._id, payload));
                setModalConfig(prev => ({ ...prev, show: false }));
            }
        });
    };

    const openPropertyDefinitionsModal = async (vendor) => {
        setSelectedVendor(vendor);

        // Fetch category table columns
        try {
            const response = await getMethod(`/raw-material-category/${vendor.category?._id}/table-columns`);
            const data = response;
            if (data.success) {
                setCategoryTableColumns(data.data.tableColumns);
            }
        } catch (error) {
            console.error('Error fetching category columns:', error);
        }

        setShowPropertyModal(true);
    };

    const handleSavePropertyDefinitions = async (propertyDefinitions) => {
        try {
            // Fix the URL - remove the extra colon and ensure correct path
            // Use your actual API base URL from environment or config
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

            const response = await fetch(`${API_BASE_URL}/raw-material-vendor/${selectedVendor._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    vendorItemPropertyDefinitions: propertyDefinitions
                })
            });

            if (response.ok) {
                const result = await response.json();
                alert('Property definitions saved successfully!');
                setShowPropertyModal(false);
                // Refresh vendors list
                dispatch(fetchAllRawMaterialVendorAction());
            } else {
                const error = await response.json();
                alert(error.message || 'Failed to save property definitions');
            }
        } catch (error) {
            console.error('Error saving property definitions:', error);
            alert('Error saving property definitions');
        }
    };

    return (
        <>
            <div className="d-flex flex-column align-items-center">
                <div className="d-flex my-4 w-100 justify-content-between">
                    <h3>Raw Material Vendor</h3>
                    <button onClick={openAddModal} className="btn btn-primary">
                        + Add Vendor
                    </button>
                </div>

                <div className="w-100">
                    <table className="table table-sm align-middle mt-3">
                        <thead className="table-secondary">
                            <tr>
                                <th className="p-3">Vendor Name</th>
                                <th className="p-3">Vendor Category</th>
                                <th className="p-3">Location</th>
                                <th className="p-3">Properties Count</th>
                                <th className="p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rawMaterialVendors?.length ? (
                                rawMaterialVendors?.map((item, index) => (
                                    <React.Fragment key={item._id}>
                                        {/* Main Row */}
                                        <tr
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setOpenRow(openRow === index ? null : index)}
                                        >
                                            <td className="p-3">
                                                <div className="d-flex align-items-center">
                                                    <span
                                                        className="me-2 transition-rotate"
                                                        style={{
                                                            display: "inline-block",
                                                            transform: openRow === index ? "rotate(180deg)" : "rotate(0deg)",
                                                            transition: "transform 0.2s ease"
                                                        }}
                                                    >
                                                        <IoIosArrowDown />
                                                    </span>
                                                    {item.name}
                                                </div>
                                            </td>
                                            <td className="p-3">{item.category?.name}</td>
                                            <td className="p-3">{item.properties?.location || '-'}</td>
                                            <td className="p-3">
                                                <span className="badge bg-info">
                                                    {item.vendorItemPropertyDefinitions?.length || 0} properties
                                                </span>
                                            </td>
                                            <td className="p-3">
                                                <button
                                                    className="btn btn-sm btn-primary me-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditModal(item);
                                                    }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-warning"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openPropertyDefinitionsModal(item);
                                                    }}
                                                >
                                                    Manage Properties
                                                </button>
                                            </td>
                                        </tr>

                                        {/* Expandable Row */}
                                        {openRow === index && (
                                            <tr>
                                                <td colSpan="5" className="bg-light p-3">
                                                    <div className="row">
                                                        {/* Vendor Details */}
                                                        <div className="col-md-6">
                                                            <h6 className="mb-3">🏢 Vendor Details</h6>
                                                            {item.properties && Object.keys(item.properties).length ? (
                                                                <table className="table table-sm table-bordered">
                                                                    <thead className="table-secondary">
                                                                        <tr>
                                                                            <th>Property</th>
                                                                            <th>Value</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {Object.entries(item.properties).map(([key, value]) => (
                                                                            <tr key={key}>
                                                                                <td className="text-capitalize">{key.replace(/([A-Z])/g, ' $1')}</td>
                                                                                <td>{value}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <div className="text-muted">No vendor details defined</div>
                                                            )}
                                                        </div>

                                                        {/* Property Definitions */}
                                                        <div className="col-md-6">
                                                            <h6 className="mb-3">📋 Item Property Definitions</h6>
                                                            {item.vendorItemPropertyDefinitions?.length ? (
                                                                <table className="table table-sm table-bordered">
                                                                    <thead className="table-secondary">
                                                                        <tr>
                                                                            <th>Name</th>
                                                                            <th>Label</th>
                                                                            <th>Type</th>
                                                                            <th>Required</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {item.vendorItemPropertyDefinitions.map((prop, idx) => (
                                                                            <tr key={idx}>
                                                                                <td><code>{prop.name}</code></td>
                                                                                <td>{prop.label}</td>
                                                                                <td>{prop.type}</td>
                                                                                <td>{prop.required ? 'Yes' : 'No'}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <div className="text-muted">No property definitions set</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted">
                                        {isLoading ? "Loading..." : "No Vendors found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Vendor Modal */}
            <DynamicFormModal
                show={modalConfig.show}
                title={modalConfig.title}
                fields={modalConfig.fields}
                initialData={modalConfig.initialData}
                onSubmit={modalConfig.onSubmit}
                dropdownData={modalConfig.dropdownData}
                onHide={() => setModalConfig(prev => ({ ...prev, show: false }))}
            />

            {/* Property Definitions Modal */}
            {showPropertyModal && selectedVendor && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Manage Item Properties for {selectedVendor.name}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowPropertyModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <VendorPropertyDefinitionForm
                                    existingProperties={selectedVendor.vendorItemPropertyDefinitions || []}
                                    onSave={handleSavePropertyDefinitions}
                                    onCancel={() => setShowPropertyModal(false)}
                                    categoryTableColumns={categoryTableColumns}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RawMaterialVendor;